import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  type DefaultHomeTab,
  resolveHomeRoute,
} from "@/constants/AppNavigation";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useColorScheme } from "@/hooks/useColorScheme";
import Feather from "@expo/vector-icons/Feather";
import {
  DarkTheme,
  DefaultTheme,
  StackActions,
  ThemeProvider,
} from "@react-navigation/native";
import { useFonts } from "expo-font";
import * as Notifications from "expo-notifications";
import {
  Redirect,
  Slot,
  Stack,
  useRootNavigation,
  useSegments,
} from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";
import "react-native-reanimated";
import "../global.css";
import "../utils/i18n";

// Firebase instances
import {
  auth as nativeAuth,
  firestore as nativeFirestore,
} from "../firebase/native";
import { auth as webAuth, firestore as webFirestore } from "../firebase/web";

// Modular web SDK helpers
import { getRedirectResult as webGetRedirectResult } from "firebase/auth";
import {
  doc as webDoc,
  onSnapshot as webOnSnapshot,
} from "firebase/firestore";
import { ensureUserDocExists } from "@/utils/createUserDoc.web";

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

if (Platform.OS === "android") {
  Notifications.setNotificationChannelAsync("default", {
    name: "default",
    importance: Notifications.AndroidImportance.MAX,
    enableVibrate: true,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: "#FF231F7C",
  });
}

SplashScreen.preventAutoHideAsync();

if (Platform.OS !== "web") {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { GoogleSignin } = require("@react-native-google-signin/google-signin");
  GoogleSignin.configure({
    webClientId: process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID,
  });
}

const isWeb = Platform.OS === "web";
const auth: any = isWeb ? webAuth : nativeAuth();
const firestore: any = isWeb ? webFirestore : nativeFirestore();

type UserDocState = {
  exists: boolean;
  defaultHomeTab?: DefaultHomeTab;
};

// Subscribe to users/{uid} for the redirect gate. Using onSnapshot (instead
// of a one-shot getDoc) is important: the first Firestore read right after
// sign-in can race the auth-token-propagation window and fail with
// `unavailable` / `deadline-exceeded`, and onSnapshot retries its underlying
// channel automatically. A one-shot get here would strand the user on the
// auth screen with onboardingComplete=false until the app was relaunched.
const subscribeUserDoc = (
  uid: string,
  onDoc: (state: UserDocState) => void,
  onError: (err: unknown) => void,
): (() => void) => {
  if (isWeb) {
    const ref = webDoc(firestore, "users", uid);
    return webOnSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
        onDoc({
          exists: snap.exists(),
          defaultHomeTab: data?.defaultHomeTab as DefaultHomeTab | undefined,
        });
      },
      (err) => onError(err),
    );
  }
  const ref = firestore.collection("users").doc(uid);
  return ref.onSnapshot(
    (snap: { exists: boolean; data: () => Record<string, unknown> | undefined }) => {
      const data = snap.exists ? snap.data() : undefined;
      onDoc({
        exists: snap.exists,
        defaultHomeTab: data?.defaultHomeTab as DefaultHomeTab | undefined,
      });
    },
    (err: unknown) => onError(err),
  );
};

const InitialLayout = () => {
  const { user, isLoading } = useAuth();
  const [userDocState, setUserDocState] = useState<UserDocState | null>(null);
  const segments = useSegments();
  const colorScheme = useColorScheme();
  const rootNavigation = useRootNavigation();

  useEffect(() => {
    if (!isLoading) {
      SplashScreen.hideAsync();
    }
  }, [isLoading]);

  useEffect(() => {
    if (!user) {
      setUserDocState(null);
      return;
    }
    const unsubscribe = subscribeUserDoc(
      user.uid,
      (state) => setUserDocState(state),
      (err) => {
        // Do NOT flip userDocState to a non-exists stub here — onSnapshot
        // retries transient errors on its own; only non-recoverable errors
        // (permission-denied etc.) land in this callback. Leaving the state
        // unchanged keeps the redirect guard showing <Slot /> instead of
        // trapping the user on the wrong route.
        console.error("Error subscribing to user doc:", err);
      },
    );
    return () => unsubscribe();
  }, [user]);

  const handleNotificationResponse = useCallback(
    (response: Notifications.NotificationResponse) => {
      const data = response.notification.request.content.data;
      if (
        (data?.type === "NEW_POST" ||
          data?.type === "GROUP_POST" ||
          data?.type === "GLOBAL_FEED_POST") &&
        data.postId &&
        rootNavigation?.isReady() &&
        user
      ) {
        rootNavigation.dispatch(
          StackActions.push("(tabs)", {
            screen: "post/[postId]",
            params: { postId: data.postId as string },
          }),
        );
      } else if (
        (data?.type === "COMMENT" ||
          data?.type === "COMMENT_ON_FOLLOWED_POST" ||
          data?.type === "LIKE") &&
        data.resourceId &&
        rootNavigation?.isReady() &&
        user
      ) {
        rootNavigation.dispatch(
          StackActions.push("(tabs)", {
            screen: "post/[postId]",
            params: { postId: data.resourceId as string },
          }),
        );
      } else if (
        data?.type === "GROUP_INVITATION" &&
        rootNavigation?.isReady() &&
        user
      ) {
        rootNavigation.dispatch(
          StackActions.push("(tabs)", { screen: "groups" }),
        );
      } else if (
        data?.type === "GROUP_ACCESS_REQUEST" &&
        rootNavigation?.isReady() &&
        user
      ) {
        if (data.groupId) {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", {
              screen: "groups/group-info",
              params: { groupId: data.groupId as string },
            }),
          );
        } else {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", { screen: "groups" }),
          );
        }
      } else if (
        data?.type === "GROUP_ACCESS_APPROVED" &&
        rootNavigation?.isReady() &&
        user
      ) {
        if (data.groupId) {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", {
              screen: "groups/[id]",
              params: { id: data.groupId as string },
            }),
          );
        } else {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", { screen: "groups" }),
          );
        }
      }
    },
    [rootNavigation, user],
  );

  useEffect(() => {
    if (Platform.OS === "web") return;
    if (!rootNavigation?.isReady() || !user) {
      return;
    }

    const responseListener =
      Notifications.addNotificationResponseReceivedListener(
        handleNotificationResponse,
      );

    Notifications.getLastNotificationResponseAsync().then((response) => {
      if (response) {
        handleNotificationResponse(response);
      }
    });

    return () => {
      responseListener.remove();
    };
  }, [rootNavigation, handleNotificationResponse, user]);

  if (isLoading) {
    return <Slot />;
  }

  // Redirect only when the user is fully onboarded (has a Firestore user doc)
  if (user && user.emailVerified) {
    if (userDocState === null) {
      return <Slot />;
    }
    if (userDocState.exists && segments[0] !== "(tabs)") {
      return (
        <Redirect href={resolveHomeRoute(userDocState.defaultHomeTab) as never} />
      );
    }
    if (!userDocState.exists && segments[0] !== "(auth)") {
      return <Redirect href="/(auth)/login" />;
    }
  }

  if (!user && segments[0] !== "(auth)") {
    return <Redirect href="/(auth)/login" />;
  }

  const themeValue = colorScheme === "dark" ? DarkTheme : DefaultTheme;

  return (
    <ThemeProvider value={themeValue}>
      <Stack>
        <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
        <Stack.Screen name="(auth)" options={{ headerShown: false }} />
        <Stack.Screen name="+not-found" />
      </Stack>
    </ThemeProvider>
  );
};

export default function RootLayout() {
  const [redirectChecked, setRedirectChecked] = useState<boolean>(false);

  useEffect(() => {
    // Only run on web
    if (isWeb) {
      let mounted = true;
      const checkRedirect = async () => {
        try {
          const result = await webGetRedirectResult(auth);
          const user = result?.user ?? auth.currentUser;
          if (user) {
            // Sign-in via redirect just landed. If there's no VehiLink doc
            // yet, create one — same shape /login and /signup write when
            // they call the helper. Both screens show terms text next to
            // the OAuth buttons, so termsAccepted=true is justified. The
            // gate then routes the user normally.
            try {
              await ensureUserDocExists(firestore, user, {
                source: "redirect-return",
              });
            } catch (helperErr) {
              console.warn(
                "RootLayout: ensureUserDocExists failed; leaving user signed in for retry from the auth screen",
                helperErr,
              );
            }
          }
        } catch (err) {
          console.warn("RootLayout: getRedirectResult errored:", err);
        } finally {
          if (mounted) setRedirectChecked(true);
        }
      };
      checkRedirect();
      return () => {
        mounted = false;
      };
    } else {
      setRedirectChecked(true);
    }
  }, []);

  const [fontsLoaded, fontError] = useFonts({
    ...Feather.font,
  });

  useEffect(() => {
    if (fontError) {
      SplashScreen.hideAsync();
      throw fontError;
    }
  }, [fontError]);

  if (!fontsLoaded || !redirectChecked) {
    return null;
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <InitialLayout />
      </AuthProvider>
    </ErrorBoundary>
  );
}
